import React from 'react'

/**
 * Setter function returned by {@link useInputState}.
 *
 * @param value - The new input value.
 * @param emit  - When `true` (default), the value is debounced and forwarded
 *                to the emitter callback. Pass `false` to update local state
 *                silently without triggering the emitter.
 */
type SetInputState = (value: string | undefined, emit?: boolean) => void

/**
 * Manages local state for a text input while debouncing change propagation.
 *
 * The hook keeps an immediate local value for responsive UI updates and
 * defers the (potentially expensive) parent callback behind a debounce timer.
 * Any pending debounced value is automatically flushed on unmount so that
 * fast interactions (e.g. type-then-immediately-submit) never lose data.
 *
 * Recommended debounce values:
 * -  50–100 ms — autocomplete / typeahead where responsiveness is critical
 * - 150–200 ms — lightweight local state propagation (model clones, form state)
 * - 300–500 ms — API calls, search queries, expensive computations
 *
 * @param emitter  - Callback invoked with the latest value after the debounce
 *                   window elapses. Also called synchronously on unmount if a
 *                   value is still pending.
 * @param debounce - Delay in milliseconds before the emitter is called.
 *                   Defaults to `320`.
 *
 * @returns A tuple of:
 *   - `value`    — The current local input value.
 *   - `setValue` — Setter that updates local state and optionally queues the emitter.
 *   - `flush`    — Immediately fires the emitter with any pending value and
 *                  cancels the debounce timer. No-op when nothing is pending.
 *
 * @example
 * ```tsx
 * const [name, setName, flushName] = useInputState((v) => onChange(v), 150)
 *
 * <Input value={name ?? model.getName()} onChange={(e) => setName(e.target.value)} />
 * ```
 */
export const useInputState = (
    emitter: (value: string) => any,
    debounce: number = 320,
): [string, SetInputState, () => void] => {
    const [value, setValue] = React.useState<string | undefined>()
    const debouncer = React.useRef<any>(undefined)
    const pendingValue = React.useRef<string | undefined>(undefined)
    const emitterRef = React.useRef(emitter)
    emitterRef.current = emitter

    /** Immediately emit any pending debounced value and clear the timer. */
    const flush = React.useCallback(() => {
        if (debouncer.current !== undefined) {
            clearTimeout(debouncer.current)
            debouncer.current = undefined
            if (pendingValue.current !== undefined) {
                emitterRef.current(pendingValue.current)
                pendingValue.current = undefined
            }
        }
    }, [])

    // Flush on unmount so pending values are never silently dropped.
    React.useEffect(() => flush, [flush])

    const setValueAndEmit = (v: string, emit: boolean = true) => {
        clearTimeout(debouncer.current)
        debouncer.current = undefined
        pendingValue.current = undefined

        if (emit) {
            pendingValue.current = v
            debouncer.current = setTimeout(() => {
                debouncer.current = undefined
                pendingValue.current = undefined
                emitter(v)
            }, debounce)
        }

        setValue(v)
    }

    return [value!, setValueAndEmit, flush]
}
